import {
  Building,
  BuildingElementType,
  BuildingViewMode,
} from '../models/Construction'
import EventEmitter, { StoreyElementsVisibility } from '../utils/EventEmitter'
import {
  ConstructionBuilder,
  SnapWallElementsPayload,
} from './constructionBuilder'
import { MaterialManager } from './materials/materialManager'
import { SceneManager } from './sceneManager'
import store from '../store'
import { setShowProgressModal } from '../actions/construction'
import { BuildingStructuralTypes } from '../models/Structural'
import { SelectionManager } from './selection/selectionManager'
import { CodeValidationRuleStatus } from '../models/Validation'
import { AbstractMesh } from '@babylonjs/core'
import { createWallGaps } from './utils'
import { BimObject } from '../models/Bim'

class ConstructionPlanner {
  constructionBuilder: ConstructionBuilder
  eventEmitter: EventEmitter

  constructor(readonly canvas: HTMLCanvasElement) {
    this.eventEmitter = new EventEmitter()
    const sceneManager = SceneManager.getInstance()
    sceneManager.initialize(this.canvas, this.eventEmitter)
    sceneManager.startRenderLoop()
    this.constructionBuilder = new ConstructionBuilder()
  }

  async getConstructionImage() {
    return SceneManager.getInstance().getScreenshot()
  }

  async createBuilding(
    building: Building,
    options?: { showAnimation?: boolean; itemIds?: string[] | undefined },
  ) {
    let sceneInstanceId = -1
    try {
      sceneInstanceId = SceneManager.sceneInstanceId()
      await this.constructionBuilder.createBuilding(
        building,
        options,
        sceneInstanceId,
      )

      if (!options?.itemIds) {
        SceneManager.getInstance()
          .getScene()
          ?.onAfterRenderObservable.addOnce(() => {
            store.dispatch<any>(setShowProgressModal(false))
          })
      }

      return SceneManager.getInstance().isActualSceneInstance(sceneInstanceId)
    } catch (error: any) {
      SceneManager.getInstance().reportError(sceneInstanceId, error)
      return false
    }
  }

  buildingCreated(building: Building) {
    this.constructionBuilder.enableEdgeRendering()
    this.constructionBuilder.enableShadows()
    this.constructionBuilder.setWallsNormalsAccordingToBuildingCenter()
    this.constructionBuilder.createStructuralElements(building)
    this.constructionBuilder.enableItemsSelection()
    this.constructionBuilder.createGrid()
    createWallGaps()

    SceneManager.getInstance().updateThumbnail()
  }

  createStructuralElements(building: Building) {
    this.constructionBuilder.createStructuralElements(building)
  }

  async createBimObjects(building: Building, bimObjects: BimObject[]) {
    return this.constructionBuilder.createBimObjects(building, bimObjects)
  }

  clearStructuralElements() {
    this.constructionBuilder.clearStructuralElements()
  }

  activateBuildingItem(id: string) {
    this.constructionBuilder.activateBuildingItem(id)
  }

  async replaceBuildingElements(
    elementsIds: string[],
    bimObject: BimObject,
    updateHole?: boolean,
  ) {
    this.constructionBuilder.replaceBuildingElements(
      elementsIds,
      bimObject,
      updateHole,
    )
  }

  changeBuildingMode(mode: BuildingViewMode, selectedItemId: string | null) {
    this.constructionBuilder.changeBuildingMode(mode, selectedItemId)
  }

  updateCameraTargetScreenOffset(x?: number, y?: number) {
    this.constructionBuilder.updateCameraTargetScreenOffset(x, y)
  }

  updateGrid() {
    MaterialManager.getInstance().updateGridMaterial()
  }

  toggleStructuralElements(types: BuildingStructuralTypes[]) {
    this.constructionBuilder.toggleStructuralElements(types)
  }

  toggleStructuralElementsPicking(pickingEnabled: boolean) {
    this.constructionBuilder.toggleStructuralElementsPicking(pickingEnabled)
  }

  toggleBuildingElementsPicking(
    elementTypes: BuildingElementType[],
    pickingEnabled: boolean,
  ) {
    this.constructionBuilder.toggleBuildingElementsPicking(
      elementTypes,
      pickingEnabled,
    )
  }

  playStructuralFadeAnimation() {
    return this.constructionBuilder.playStructuralFadeAnimation()
  }

  async playCodeValidationAnimation(data: CodeValidationRuleStatus[]) {
    return this.constructionBuilder.playCodeValidationAnimation(data)
  }

  removeBuildingElement(element: AbstractMesh) {
    this.constructionBuilder.removeBuildingElement(element)
  }

  snapTwoWallsElements(data: SnapWallElementsPayload) {
    this.constructionBuilder.snapTwoWallsElements(data)
  }

  toggleWallTransparency(isEnabled: boolean) {
    if (isEnabled) {
      this.constructionBuilder.enableWallTransparencyMode()
    } else {
      this.constructionBuilder.disableWallTransparencyMode()
    }
  }

  changeStoreyElementVisibility(hiddenElements: StoreyElementsVisibility) {
    this.constructionBuilder.changeStoreyElementVisibility(hiddenElements)
  }

  toggleGridVisibility(isVisible: boolean) {
    this.constructionBuilder.toggleGridVisibility(isVisible)
  }

  toggleRoofVisibility(isVisible: boolean) {
    this.constructionBuilder.toggleRoofVisibility(isVisible)
  }

  enableCreateWallPickDownTrigger() {
    this.constructionBuilder.enableCreateWallPickDownTrigger()
  }

  enableCreateWindowPickDownTrigger() {
    this.constructionBuilder.enableCreateWindowPickDownTrigger()
  }

  enableCreateDoorPickDownTrigger() {
    this.constructionBuilder.enableCreateDoorPickDownTrigger()
  }

  enableCreateBeamPickDownTrigger() {
    this.constructionBuilder.enableCreateBeamPickDownTrigger()
  }

  enableEditBeamOnPickDownTrigger() {
    this.constructionBuilder.enableEditBeamOnPickDownTrigger()
  }

  disableElementPickDownTrigger() {
    this.constructionBuilder.disableElementPickDownTrigger()
  }

  addPickTriggers(itemIds: string[]) {
    this.constructionBuilder.addPickTriggers(itemIds)
  }

  enableMarqueeSelection() {
    this.constructionBuilder.enableMarqueeSelection()
  }

  disableMarqueeSelection() {
    this.constructionBuilder.disableMarqueeSelection()
  }

  dispose() {
    this.eventEmitter.dispose()
    SceneManager.getInstance().dispose()
    MaterialManager.getInstance().dispose()
    SelectionManager.getInstance().dispose()
  }
}

export default ConstructionPlanner
